import { openDB, deleteDB } from 'idb';

const DB_NAME = 'FilterDatabase';
const STORE_NAME = 'filterStore';
const DB_VERSION = 1;

// Define unique keys to identify our two pieces of data in the database
const CURRENT_FILTER_KEY = 'currentFilterData';
const PREVIOUS_FILTER_KEY = 'previousFilterData';

// Function to connect to/create the database
async function getDB() {
  return openDB(DB_NAME, DB_VERSION, {
    upgrade(db) {
      // Create the 'object store' (like a table) if it doesn't exist
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        // We'll store objects like { key: 'ourKey', value: ourData }
        // 'key' will be our primary key (CURRENT_FILTER_KEY or PREVIOUS_FILTER_KEY)
        db.createObjectStore(STORE_NAME, { keyPath: 'key' });
      }
    },
  });
}

/**
 * Saves data to IndexedDB under a specific key.
 * @param {string} key - Either CURRENT_FILTER_KEY or PREVIOUS_FILTER_KEY
 * @param {any} data - The filter data object to save.
 */
export async function saveDbFilterData(key, data) {
  try {
    const db = await getDB();
    // Use 'readwrite' transaction for saving
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    // 'put' will add or update the record based on the key
    await store.put({ key: key, value: data });
    await tx.done; // Wait for transaction to complete
  } catch (error) {
    console.error(`DB Error: Failed to save data for key ${key}`, error);
    throw error; // Re-throw to signal failure
  }
}

/**
 * Gets data from IndexedDB for a specific key.
 * @param {string} key - Either CURRENT_FILTER_KEY or PREVIOUS_FILTER_KEY
 * @returns {Promise<any|null>} The retrieved data, or null if not found or on error.
 */
export async function getDbFilterData(key) {
  try {
    const db = await getDB();
    // Use 'readonly' transaction for reading
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const record = await store.get(key);
    await tx.done;
    // If a record was found, return its 'value' property, otherwise null
    return record ? record.value : null;
  } catch (error) {
    console.error(`DB Error: Failed to get data for key ${key}`, error);
    return null; // Return null on error
  }
}

export async function clearFilterDatabase() {
  try {
    console.log(`Attempting to delete database: ${DB_NAME}`);
    await deleteDB(DB_NAME); // <-- The core one-liner logic
    console.log(`Database "${DB_NAME}" deleted successfully.`);
  } catch (error) {
    console.error(`Failed to delete database "${DB_NAME}":`, error);
  }
}

// Export the keys so we use the same strings everywhere
export { CURRENT_FILTER_KEY, PREVIOUS_FILTER_KEY };